import React, { useCallback, useRef, useState } from 'react';
import { State as GridState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteProps, ListItemProps } from '@progress/kendo-react-dropdowns';
import { debounce } from 'ts-debounce';
import { fetchApi } from '../../services/api';

interface PartViewModel {
  PartID: number;
  PartNumber: string;
  Description?: string;
  QuantityInStock: number;
  UnitOfMeasure: string;
  AverageCost: number;
  IsActive: boolean;
}

interface PartAutoCompleteViewModel extends PartViewModel {
  DisplayValue: string;
}

interface PartSearchResponse {
  Items: {
    Data: PartViewModel[];
    Total: number;
  };
}

type Props = {
  onSelectedPart: (part: PartViewModel | null) => void;
  selectedPartID?: number;
  setFocus?: boolean;
  onlyActiveParts?: boolean;
} & Omit<AutoCompleteProps, 'data' | 'value' | 'onChange'>;

export const GetPartAutoCompleteDisplayValue = (part: PartViewModel): string => {
  const stockInfo = part.QuantityInStock > 0 ? ` (${part.QuantityInStock} ${part.UnitOfMeasure} in stock)` : ' (Out of stock)';
  const description = part.Description ? ` - ${part.Description}` : '';
  return `${part.PartNumber}${description}${stockInfo}`;
};

const PartAutoComplete: React.FC<Props> = ({
  onSelectedPart,
  selectedPartID,
  setFocus,
  onlyActiveParts = true,
  ...autoCompleteProps
}) => {
  const inputAC = useRef<AutoComplete>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [partDropDownListing, setPartDropDownListing] = useState<PartAutoCompleteViewModel[]>([]);
  const [partAutoCompleteValue, setPartAutoCompleteValue] = useState('');
  const _selectedPart = useRef<number>(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearchInput = useCallback(
    debounce((val: string) => {
      if (val.length >= 2 && _selectedPart.current === 0) {
        getPartListing(val);
      }
    }, 750), [onlyActiveParts]);

  const getPartListing = async (searchTerm: string) => {
    setLoading(true);
    try {
      const filters: GridState['filter']['filters'] = [
        {
          logic: 'or',
          filters: [
            { field: 'PartNumber', operator: 'contains', value: searchTerm },
            { field: 'Description', operator: 'contains', value: searchTerm }
          ]
        }
      ];
      if (onlyActiveParts) {
        filters.unshift({ field: 'IsActive', operator: 'eq', value: true });
      }

      const queryString = toDataSourceRequestString({
        skip: 0,
        take: 50,
        filter: { logic: 'and', filters }
      });

      const response: PartSearchResponse = await fetchApi(`/api/ShopInventory/Parts?${queryString}`);
      const partsWithDisplay: PartAutoCompleteViewModel[] = response.Items.Data.map(part => ({
        ...part,
        DisplayValue: GetPartAutoCompleteDisplayValue(part)
      }));

      setPartDropDownListing(partsWithDisplay);
    } catch (error) {
      console.error('Error loading parts:', error);
      setPartDropDownListing([]);
    } finally {
      setLoading(false);
    }
  };

  const renderDisplayName = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
    const itemChildren = <span>{itemProps.dataItem.DisplayValue}</span>;
    return React.cloneElement(li, li.props, itemChildren);
  }

  const onPartChange = (event: AutoCompleteChangeEvent) => {
    if (typeof event.target.value === 'object') {
      const partObj = event.target.value as any;
      setPartAutoCompleteValue(partObj.DisplayValue);
      _selectedPart.current = partObj.PartID;
      onSelectedPart(partObj);
    } else {
      setPartAutoCompleteValue(event.target.value);
      _selectedPart.current = 0;
      onSelectedPart(null);
      debounceSearchInput(event.target.value);
    }
  };

  React.useEffect(() => {
    if (setFocus && inputAC.current) {
      inputAC.current.focus();
    }
  }, [setFocus]);

  React.useEffect(() => {
    if (selectedPartID && selectedPartID !== _selectedPart.current) {
      // Load selected part data
      const loadSelectedPart = async () => {
        try {
          const response: { Part: PartViewModel } = await fetchApi(`/api/ShopInventory/PartDetail?PartID=${selectedPartID}`);
          const part = response.Part;
          const partWithDisplay: PartAutoCompleteViewModel = {
            ...part,
            DisplayValue: GetPartAutoCompleteDisplayValue(part)
          };
          
          _selectedPart.current = part.PartID;
          setPartAutoCompleteValue(partWithDisplay.DisplayValue);
          onSelectedPart(part);
        } catch (error) {
          console.error('Error loading selected part:', error);
        }
      };
      
      loadSelectedPart();
    }
  }, [selectedPartID, onSelectedPart]);

  return (
    <AutoComplete
      ref={inputAC}
      data={partDropDownListing}
      value={partAutoCompleteValue}
      loading={loading}
      placeholder="Enter part # or description..."
      itemRender={renderDisplayName}
      dataItemKey="PartID"
      onChange={onPartChange}
      {...autoCompleteProps}
    />
  );
};

export default PartAutoComplete;
